import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, IconButton, Grid, Paper,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Pagination, Drawer, List, ListItem, ListItemText,
  Divider, Collapse, Select, MenuItem, FormControl, InputLabel, Tooltip, Autocomplete,
  useTheme, useMediaQuery, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { Refresh, ExitToApp, Close, Edit, Delete, Warning, Settings, Add, DragIndicator, KeyboardArrowDown, KeyboardArrowRight, KeyboardArrowLeft, LightMode, DarkMode, Shuffle, VisibilityOff, Visibility, HourglassEmpty } from '@mui/icons-material';
import BrandIcon from '../components/BrandIcon';
import HubHeader from '../components/HubHeader';
import { useHubAuth } from '../contexts/HubAuthContext';
import { useThemeMode, THEME_COLORS, ThemeColorName } from '../contexts/ThemeContext';
import { COUNTRIES } from '../data/countries';
import { correctedWcoWidth } from '../utils/wcoGeometry';
import axios from 'axios';

const HUB_API = process.env.REACT_APP_HUB_API_URL || '';
const CARDS_PER_PAGE = 16;

interface Suite {
  id: number;
  display_name: string;
  suite_url: string;
  suite_username: string;
  position: number;
  cached_total: number;
  cached_by_modality: string;
  cached_by_priority: string;
  cached_by_modality_priority: string;
  cached_center_name: string;
  cache_updated_at: string;
  last_status: string;
  cached_role?: string;
}

const ROLE_MODAL_ROUTES: Record<string, { desktop: string; mobile: string; title: string }> = {
  radiologist: { desktop: '/reading-module', mobile: '/mobile-reading-module', title: 'Reading Module' },
  admin: { desktop: '/study-management', mobile: '/mobile-view', title: 'Study Management' },
  viewer: { desktop: '/viewer', mobile: '/mobile-view', title: 'Viewer' },
};

// Whole-card click (no modality) override — admin lands on the dashboard instead
// of Study Management. Modality-chip clicks are unaffected (still ROLE_MODAL_ROUTES).
const ROLE_CARD_ROUTES: Record<string, { desktop: string; mobile: string; title: string }> = {
  admin: { desktop: '/dashboard', mobile: '/dashboard', title: 'Dashboard' },
};

const MODALITY_COLORS: Record<string, string> = {
  DX: '#2196F3', US: '#4CAF50', CT: '#FF9800', MR: '#9C27B0', CR: '#00BCD4'
};

// Clean card — no action buttons
const SuiteCard: React.FC<{
  suite: Suite;
  onOpen: (suite: Suite, modality?: string) => void;
}> = ({ suite, onOpen }) => {
  const byModality = JSON.parse(suite.cached_by_modality || '{}');
  const byModalityPriority = JSON.parse(suite.cached_by_modality_priority || '{}');
  const getChipColor = (mod: string): string => {
    const mp = byModalityPriority[mod] || {};
    if ((mp.STAT || 0) > 0) return '#F44336';
    if ((mp.Urgent || 0) > 0) return '#FF9800';
    return '#4CAF50';
  };
  const isOffline = suite.last_status === 'offline';
  const isAuthFailed = suite.last_status === 'auth_failed';
  const hasIssue = isOffline || isAuthFailed;

  return (
    <Paper
      elevation={hasIssue ? 1 : 3}
      sx={{
        p: 2, minHeight: 80, height: '100%', cursor: hasIssue ? 'default' : 'pointer',
        opacity: hasIssue ? 0.6 : 1,
        border: hasIssue ? '1px solid rgba(255,0,0,0.3)' : '1px solid transparent',
        transition: 'all 0.2s',
        '&:hover': hasIssue ? {} : { borderColor: 'primary.main', transform: 'translateY(-2px)' }
      }}
      onClick={() => !hasIssue && onOpen(suite)}
    >
      <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ mb: 1 }}>
        {suite.display_name || suite.cached_center_name}
      </Typography>

      {hasIssue ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Warning color="error" sx={{ fontSize: 18 }} />
          <Typography variant="caption" color="error">
            {isAuthFailed ? 'Check credentials' : 'Unreachable'}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(() => {
              const entries = Object.entries(byModality);
              const MAX = 5;
              const shown = entries.slice(0, MAX);
              const overflow = entries.length - MAX;
              return (
                <>
                  {shown.map(([mod, count]: [string, any]) => (
                    <Chip
                      key={mod}
                      label={`${mod} ${count}`}
                      size="small"
                      onClick={e => { e.stopPropagation(); onOpen(suite, mod); }}
                      sx={{
                        bgcolor: getChipColor(mod),
                        color: 'white', fontWeight: 'bold', fontSize: '0.7rem',
                        cursor: 'pointer', '&:hover': { opacity: 0.8 }
                      }}
                    />
                  ))}
                  {overflow > 0 && (
                    <Chip label={`+${overflow}`} size="small" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }} />
                  )}
                  {entries.length === 0 && suite.cached_total !== 0 && (
                    <Typography variant="caption" color="text.secondary">No modality data</Typography>
                  )}
                </>
              );
            })()}
          </Box>
          {suite.cached_total === 0 && (
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold', mt: 0.5, display: 'block' }}>
              ✓ All clear
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};


const QUOTES = [
  { text: "The good physician treats the disease; the great physician treats the patient.", author: "William Osler" },
  { text: "Wherever the art of medicine is loved, there is also a love of humanity.", author: "Hippocrates" },
  { text: "The art of medicine consists of amusing the patient while nature cures the disease.", author: "Voltaire" },
  { text: "Medicine is a science of uncertainty and an art of probability.", author: "William Osler" },
  { text: "To study the phenomena of disease without books is to sail an uncharted sea.", author: "William Osler" },
  { text: "The practice of medicine is an art, not a trade; a calling, not a business.", author: "William Osler" },
  { text: "Diagnosis is not the end, but the beginning of practice.", author: "Martin H. Fischer" },
  { text: "The best doctor gives the least medicine.", author: "Benjamin Franklin" },
  { text: "A physician who treats himself has a fool for a patient.", author: "William Osler" },
  { text: "Observation, reason, human understanding, courage — these make the physician.", author: "Martin H. Fischer" },
  { text: "In nothing do men more nearly approach the gods than in giving health to men.", author: "Cicero" },
  { text: "One of the first duties of the physician is to educate the masses not to take medicine.", author: "William Osler" },
  { text: "The secret of the care of the patient is in caring for the patient.", author: "Francis W. Peabody" },
  { text: "Radiology is the art of reading light and shadow to find the truth within.", author: "Unknown" },
  { text: "Every patient carries their own doctor inside them.", author: "Albert Schweitzer" },
  { text: "The doctor of the future will give no medicine, but will interest patients in the human frame.", author: "Thomas Edison" },
  { text: "Do not go where the path may lead; go instead where there is no path and leave a trail.", author: "Ralph Waldo Emerson" },
  { text: "Excellence is not a destination but a continuous journey that never ends.", author: "Brian Tracy" },
  { text: "The function of protecting and developing health must rank even above that of restoring it.", author: "Herbert Spencer" },
  { text: "Attention to detail is the hallmark of the radiologist's craft.", author: "Unknown" },
  { text: "First, do no harm.", author: "Hippocrates" },
  { text: "The most important thing in communication is hearing what isn't said.", author: "Peter Drucker" },
  { text: "In the middle of difficulty lies opportunity.", author: "Albert Einstein" },
  { text: "Quality is never an accident; it is always the result of intelligent effort.", author: "John Ruskin" },
  { text: "The whole is greater than the sum of its parts.", author: "Aristotle" },
  { text: "Medicine is learned by the bedside and not in the classroom.", author: "William Osler" },
  { text: "Precision is the soul of radiology.", author: "Unknown" },
  { text: "Strive not to be a success, but rather to be of value.", author: "Albert Einstein" },
  { text: "The greatest discovery of any generation is that a human being can alter his life by altering his attitude.", author: "William James" },
  { text: "It always seems impossible until it's done.", author: "Nelson Mandela" },
  { text: "Wear the white coat with dignity and pass on your knowledge and clinical skills to others.", author: "Ben Carson" },
  { text: "Listen to your patient, he is telling you the diagnosis.", author: "William Osler" },
  { text: "There is no such thing as a routine study; every image tells a patient's story.", author: "Unknown" },
  { text: "The eye sees only what the mind is prepared to comprehend.", author: "Robertson Davies" },
  { text: "In science, the credit goes to the man who convinces the world, not to whom the idea first occurs.", author: "William Osler" },
  { text: "Nothing in life is to be feared, it is only to be understood.", author: "Marie Curie" },
  { text: "The art of medicine consists in keeping the patient amused while nature heals the disease.", author: "Voltaire" },
  { text: "A wise man ought to realize that health is his most valuable possession.", author: "Hippocrates" },
  { text: "It is more important to know what sort of patient has a disease than what sort of disease a patient has.", author: "William Osler" },
  { text: "Look well to this day, for it is life, the very life of life.", author: "Kalidasa" },
  { text: "Success is the sum of small efforts, repeated day in and day out.", author: "Robert Collier" },
  { text: "The soul of radiology is patience — with the image, and with the process of seeing.", author: "Unknown" },
  { text: "What we see depends mainly on what we look for.", author: "John Lubbock" },
  { text: "Genius is one percent inspiration and ninety-nine percent perspiration.", author: "Thomas Edison" },
  { text: "The good of the patient is the highest law.", author: "Hippocratic tradition" },
  { text: "In examinations, those who do not wish to know, ask questions in order to appear knowledgeable.", author: "William Osler" },
  { text: "Every accomplishment starts with the decision to try.", author: "John F. Kennedy" },
  { text: "The physician must be able to tell the antecedents, know the present, and foretell the future.", author: "Hippocrates" },
  { text: "There are no shortcuts to any place worth going.", author: "Beverly Sills" },
  { text: "The greatest medicine of all is teaching people how not to need it.", author: "Hippocrates" },
  { text: "Quality means doing it right when no one is looking.", author: "Henry Ford" },
  { text: "A good clinician knows the value of a good history; a good radiologist knows the value of a good look.", author: "Unknown" },
  { text: "Simplicity is the ultimate sophistication.", author: "Leonardo da Vinci" },
  { text: "The important thing is not to stop questioning. Curiosity has its own reason for existing.", author: "Albert Einstein" },
  { text: "Care more than others think wise; risk more than others think safe.", author: "Howard Schultz" },
  { text: "The mind is not a vessel to be filled, but a fire to be kindled.", author: "Plutarch" },
  { text: "Well done is better than well said.", author: "Benjamin Franklin" },
  { text: "Medicine is not only a science; it is also an art.", author: "Paracelsus" },
  { text: "The expert in anything was once a beginner.", author: "Helen Hayes" },
  { text: "A problem well stated is a problem half solved.", author: "Charles Kettering" },
  { text: "Details create the big picture.", author: "Sanford I. Weill" },
  { text: "The mind that opens to a new idea never returns to its original size.", author: "Albert Einstein" },
  { text: "Small daily improvements are the key to staggering long-term results.", author: "James Clear" },
];

const pickRandomQuoteIndex = (currentIndex: number): number => {
  if (QUOTES.length <= 1) return 0;
  let idx = currentIndex;
  while (idx === currentIndex) {
    idx = Math.floor(Math.random() * QUOTES.length);
  }
  return idx;
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Always pads to 6 full weeks (42 cells) so the widget's height never changes month to month
const getCalendarCells = (year: number, month: number): (number | null)[] => {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length < 42) cells.push(null);
  return cells;
};

const HubDashboard: React.FC = () => {
  const { token, logout } = useHubAuth();
  const { mode, toggleTheme, themeColor, setThemeColor } = useThemeMode();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMobile = isPhone || isTablet;
  // Auto-detecting a real 13" tablet by viewport width alone doesn't work: its
  // landscape CSS width (~1000-1400px) is well past the isTablet breakpoint
  // above and overlaps ordinary desktop monitor widths. A `pointer: coarse` +
  // `hover: none` media query was tried first, but a tablet with a keyboard/
  // trackpad accessory attached (common on 13" tablets) reports `hover: hover`
  // and `pointer: fine` for that session — the trackpad emulates a precise
  // pointer, so the media query reflects the currently-attached input, not the
  // device itself, and Auto fell through to Desktop anyway.
  // `navigator.maxTouchPoints` is a static hardware capability instead — it
  // stays true regardless of what's plugged in, since the touchscreen itself
  // never goes away. Read once (touch hardware doesn't change mid-session);
  // used only to resolve 'auto' in getModalUrl below, doesn't touch this page's
  // own isMobile-driven responsive layout above.
  const [hasTouchScreen] = useState(() => typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0);
  const [suites, setSuites] = useState<Suite[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);

  // Settings drawer
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [suitesExpanded, setSuitesExpanded] = useState(false);
  const [appearanceExpanded, setAppearanceExpanded] = useState(false);
  const [appearanceSaving, setAppearanceSaving] = useState(false);
  const [displayExpanded, setDisplayExpanded] = useState(false);
  // Which Reading Module (and modal viewer) opening a suite embeds — 'auto' follows this
  // device's own viewport (see isMobile below), 'desktop'/'mobile'/'tablet' pin it regardless
  // of device. 'tablet' opens the same Mobile page as 'mobile' (see getModalUrl below) but
  // flags it via a ?display=tablet query param so the Suite side renders a smaller OHIF zoom
  // and desktop's 3-way Previous/Both/Current compare instead of phone's 2-way toggle.
  // Per-browser preference by design (localStorage only, no backend field), same as the Suite
  // Dashboard's own readingModulePreference.
  const [readingModuleDisplay, setReadingModuleDisplay] = useState<'auto' | 'desktop' | 'mobile' | 'tablet'>(() => {
    const stored = localStorage.getItem('hub_reading_module_display');
    return stored === 'desktop' || stored === 'mobile' || stored === 'tablet' ? stored : 'auto';
  });

  // Add / Edit dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editSuite, setEditSuite] = useState<Suite | null>(null);
  const [formData, setFormData] = useState({ display_name: '', suite_url: '', suite_username: '', suite_password: '' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Delete confirmation
  const [deleteSuite, setDeleteSuite] = useState<Suite | null>(null);

  // Reading modal
  const [modalSuite, setModalSuite] = useState<{ suite: Suite; modality?: string } | null>(null);
  const [modalToken, setModalToken] = useState('');
  const modalIframeRef = useRef<HTMLIFrameElement>(null);

  // getModalUrl() below measures the real titlebar rect ONCE, at the moment
  // the iframe src is built - correct for the common case, but if the user
  // toggles the native title bar (Chrome's own "Hide title bar" control on an
  // installed PWA window) *while* the Reading Modal is already open, that
  // one-time snapshot goes stale: Suite's header keeps padding for the old
  // (now wrong) geometry while the OS's caption buttons/overlay icons
  // reposition for the new one, so they visibly overlap Suite's own header
  // content. Reloading the iframe to pick up fresh query params would lose
  // the user's place in the study, so instead re-measure on every
  // geometrychange and forward the new rect into the already-open iframe
  // live via postMessage (see useEmbeddedTitlebarGeometry in Suite's
  // wcoGeometry.ts for the receiving side).
  useEffect(() => {
    if (!modalSuite) return undefined;
    const wco = (navigator as any).windowControlsOverlay;
    if (!wco) return undefined;
    const handleGeometryChange = () => {
      const rect = wco.getTitlebarAreaRect();
      const targetOrigin = (() => {
        try { return new URL(modalSuite.suite.suite_url).origin; } catch { return '*'; }
      })();
      modalIframeRef.current?.contentWindow?.postMessage(
        { source: 'hub-wco-geometry', height: Math.round(rect.height), x: Math.round(rect.x), width: Math.round(correctedWcoWidth(rect.width)) },
        targetOrigin,
      );
    };
    wco.addEventListener('geometrychange', handleGeometryChange);
    return () => wco.removeEventListener('geometrychange', handleGeometryChange);
  }, [modalSuite]);

  // The Reading Modal loads a Suite (cross-origin) page in an iframe, so iOS's
  // pinch-zoom is governed by this (Hub) page's own viewport, not the iframe's -
  // native pinch-zoom stacked on an input's auto-zoom-on-focus has been observed
  // to crash mobile Safari here (heavy GPU layers in the nested viewer). Lock it
  // while the dialog is open; the iframe's own content (e.g. OHIF image zoom) is
  // unaffected since that lives inside the iframe.
  useEffect(() => {
    if (!modalSuite) return undefined;
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const previousContent = viewportMeta?.getAttribute('content') ?? null;
    if (viewportMeta) {
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    }
    return () => {
      if (viewportMeta && previousContent !== null) {
        viewportMeta.setAttribute('content', previousContent);
      }
    };
  }, [modalSuite]);

  const [hubName, setHubName] = useState('RT Radiology Hub');
  const [hubVersion, setHubVersion] = useState('');
  const [hubSettingsExpanded, setHubSettingsExpanded] = useState(false);
  const [hubNameInput, setHubNameInput] = useState('');
  const [hubNameSaving, setHubNameSaving] = useState(false);

  // Timezone + clock
  const [timezone, setTimezone] = useState('UTC+8');
  const [hubTimezoneInput, setHubTimezoneInput] = useState('UTC+8');
  const [timezoneExpanded, setTimezoneExpanded] = useState(false);
  const [clockTime, setClockTime] = useState(new Date());

  // Location + weather — independent of Timezone, has its own settings section/Save
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [weather, setWeather] = useState<{ temp: number; code: number } | null>(null);
  const [locationMode, setLocationMode] = useState<'auto' | 'manual'>('auto');

  const [weatherExpanded, setWeatherExpanded] = useState(false);
  const [countryInput, setCountryInput] = useState('');
  const [cityQuery, setCityQuery] = useState('');
  type CityOption = { name: string; admin1?: string; country: string; latitude: number; longitude: number };
  const [cityOptions, setCityOptions] = useState<CityOption[]>([]);
  const [cityOptionsLoading, setCityOptionsLoading] = useState(false);
  const [citySelected, setCitySelected] = useState<CityOption | null>(null);

  // Account (username/password)
  const [accountExpanded, setAccountExpanded] = useState(false);
  const [accountCurrentPassword, setAccountCurrentPassword] = useState('');
  const [accountNewUsername, setAccountNewUsername] = useState('');
  const [accountNewPassword, setAccountNewPassword] = useState('');
  const [accountConfirmPassword, setAccountConfirmPassword] = useState('');
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');
  const [accountSaving, setAccountSaving] = useState(false);

  // Drag-and-drop reorder (localStorage) — pointer-based; native HTML5 DnD doesn't
  // reliably fire dragstart on trackpad drags, so this uses mousedown + window mousemove/mouseup.
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [localOrder, setLocalOrder] = useState<number[]>(() => {
    const saved = localStorage.getItem('hub_suite_order');
    return saved ? JSON.parse(saved) : [];
  });
  const rowRefs = useRef(new Map<number, HTMLElement>());
  const dragOverIdRef = useRef<number | null>(null);

  // Statistics modal
  const [statsOpen, setStatsOpen] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState<'today'|'week'|'month'|'year'>('week');
  const [statsMonth, setStatsMonth] = useState<number>(new Date().getMonth() + 1);
  const [statsYear, setStatsYear] = useState<number>(new Date().getFullYear());
  const [statsData, setStatsData] = useState<any[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);

  // Notes
  const [notes, setNotes] = React.useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notes2, setNotes2] = React.useState('');
  const [notes2Saving, setNotes2Saving] = useState(false);

  // Quote — cycle (per-visit) + hide (per-device, persisted)
  const [quoteIndex, setQuoteIndex] = useState(() => new Date().getDate() % QUOTES.length);
  const [quoteHidden, setQuoteHidden] = useState(() => localStorage.getItem('hub_quote_hidden') === 'true');

  // Calendar — visual only, no persistence
  const [calMonth, setCalMonth] = useState(() => new Date().getMonth());
  const [calYear, setCalYear] = useState(() => new Date().getFullYear());

  const headers = { Authorization: `Bearer ${token}` };

  const loadSuites = useCallback(async () => {
    try {
      const resp = await axios.get(`${HUB_API}/api/v1/suites/`, { headers });
      setSuites(resp.data as Suite[]);
    } catch { } finally { setLoading(false); }
  }, [token]);

  const loadSettings = useCallback(async () => {
    try {
      const resp = await axios.get<{ hub_name: string; timezone?: string; city?: string; country?: string; latitude?: number | null; longitude?: number | null; notes?: string; notes2?: string; theme_color?: string; location_mode?: string }>(`${HUB_API}/api/v1/settings/`, { headers });
      if (resp.data.hub_name) {
        setHubName(resp.data.hub_name);
        setHubNameInput(resp.data.hub_name);
      }
      const mode = resp.data.location_mode === 'manual' ? 'manual' : 'auto';
      setLocationMode(mode);
      // Pre-fill the Manual form's own fields regardless of current mode, so switching to
      // Manual later isn't blank. Only apply to the *displayed* city/country/lat/lon (what
      // the weather widget/tooltip use) when the saved mode is actually Manual — in Auto mode
      // those are owned by the IP-detect effect instead, so we don't stomp on it with stale data.
      if (resp.data.country !== undefined) setCountryInput(resp.data.country);
      if (resp.data.latitude != null && resp.data.longitude != null && resp.data.city) {
        setCitySelected({ name: resp.data.city, country: resp.data.country || '', latitude: resp.data.latitude, longitude: resp.data.longitude });
      }
      if (mode === 'manual') {
        if (resp.data.city !== undefined) setCity(resp.data.city);
        if (resp.data.country !== undefined) setCountry(resp.data.country);
        if (resp.data.latitude != null && resp.data.longitude != null) {
          setLatitude(resp.data.latitude);
          setLongitude(resp.data.longitude);
        }
      }
      if (resp.data.notes !== undefined) setNotes(resp.data.notes);
      if (resp.data.notes2 !== undefined) setNotes2(resp.data.notes2);
      if (resp.data.theme_color && resp.data.theme_color in THEME_COLORS) {
        setThemeColor(resp.data.theme_color as ThemeColorName);
      }
    } catch {}
  }, [token, setThemeColor]);

  const refreshCounts = useCallback(async () => {
    setRefreshing(true);
    try {
      await axios.post(`${HUB_API}/api/v1/suites/refresh`, {}, { headers });
      await loadSuites();
    } catch { } finally { setRefreshing(false); }
  }, [token, loadSuites]);

  const refreshOneSuite = useCallback(async (suiteId: number) => {
    try {
      await axios.post(`${HUB_API}/api/v1/suites/${suiteId}/refresh`, {}, { headers });
      await loadSuites();
    } catch { }
  }, [token, loadSuites]);

  const fetchCensus = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = new URLSearchParams({ period: statsPeriod });
      if (statsPeriod === 'month' || statsPeriod === 'year') params.append('year', String(statsYear));
      if (statsPeriod === 'month') params.append('month', String(statsMonth));
      const resp = await axios.get<{ results: any[] }>(`${HUB_API}/api/v1/suites/census?${params}`, { headers });
      setStatsData(resp.data.results || []);
    } catch {}
    finally { setStatsLoading(false); }
  }, [statsPeriod, statsMonth, statsYear, token]);

  // Clock ticker
  useEffect(() => {
    const timer = setInterval(() => setClockTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Hub version — shown only in Settings, not the header (mirrors Suite's own /api/v1/info)
  useEffect(() => {
    axios.get<{ version?: string }>(`${HUB_API}/api/v1/info`).then(r => {
      if (r.data?.version) setHubVersion(r.data.version);
    }).catch(() => {});
  }, []);

  // Weather — fetches straight from the stored lat/lon (set once, when a city is picked from the
  // Weather settings' Autocomplete) via Open-Meteo (no API key, CORS-open). No runtime geocoding here.
  // Cached in localStorage per coordinate so a page reload doesn't immediately re-fetch.
  useEffect(() => {
    if (latitude == null || longitude == null) { setWeather(null); return; }
    const cacheKey = `hub_weather_${latitude.toFixed(2)}_${longitude.toFixed(2)}`;
    let cancelled = false;

    const fetchWeather = async () => {
      try {
        const wx = await axios.get('https://api.open-meteo.com/v1/forecast', {
          params: { latitude, longitude, current_weather: true },
          timeout: 8000,
        });
        if (cancelled) return;
        const temp = Math.round((wx.data as any).current_weather.temperature);
        const code = (wx.data as any).current_weather.weathercode;
        setWeather({ temp, code });
        localStorage.setItem(cacheKey, JSON.stringify({ temp, code, ts: Date.now() }));
      } catch { }
    };

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.ts < 30 * 60 * 1000) setWeather({ temp: parsed.temp, code: parsed.code });
      } catch { }
    }
    fetchWeather();
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [latitude, longitude]);

  // Auto location — resolves the current viewer's own lat/lon/city/country from their IP via
  // ipwho.is (no key, HTTPS, free) whenever mode is Auto. Purely client-side/in-memory: never
  // persisted to settings, so switching back to Manual restores the admin's saved city untouched.
  useEffect(() => {
    if (locationMode !== 'auto') return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get('https://ipwho.is/', { timeout: 8000 });
        if (cancelled || (resp.data as any)?.success === false) return;
        const data = resp.data as any;
        if (typeof data.latitude === 'number' && typeof data.longitude === 'number') {
          setLatitude(data.latitude);
          setLongitude(data.longitude);
        }
        if (data.city) setCity(data.city);
        if (data.country) setCountry(data.country);
      } catch { }
    })();
    return () => { cancelled = true; };
  }, [locationMode]);

  // City search — live-queries Open-Meteo geocoding as the user types in the Weather settings
  // Autocomplete, filtered to the selected Country so results are unambiguous.
  useEffect(() => {
    if (cityQuery.trim().length < 2) { setCityOptions([]); return; }
    let cancelled = false;
    setCityOptionsLoading(true);
    const t = setTimeout(async () => {
      try {
        const resp = await axios.get('https://geocoding-api.open-meteo.com/v1/search', {
          params: { name: cityQuery.trim(), count: 10, language: 'en' },
          timeout: 8000,
        });
        if (cancelled) return;
        let results: any[] = (resp.data as any).results || [];
        const countryObj = COUNTRIES.find(c => c.name === countryInput);
        if (countryObj) results = results.filter(r => r.country_code === countryObj.code);
        setCityOptions(results.map(r => ({
          name: r.name, admin1: r.admin1, country: r.country, latitude: r.latitude, longitude: r.longitude,
        })));
      } catch { setCityOptions([]); }
      finally { if (!cancelled) setCityOptionsLoading(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [cityQuery, countryInput]);

  useEffect(() => {
    if (statsOpen) fetchCensus();
  }, [statsOpen, statsPeriod, statsMonth, statsYear, fetchCensus]);

  useEffect(() => {
    loadSuites();
    loadSettings();
    const interval = setInterval(refreshCounts, 60000);
    return () => clearInterval(interval);
  }, [loadSuites, loadSettings, refreshCounts]);

  // Apply local order to suites
  const orderedSuites = useMemo(() => {
    if (localOrder.length === 0) return suites;
    const orderMap = new Map(localOrder.map((id, idx) => [id, idx]));
    return [...suites].sort((a, b) => {
      const posA = orderMap.has(a.id) ? orderMap.get(a.id)! : 9999;
      const posB = orderMap.has(b.id) ? orderMap.get(b.id)! : 9999;
      return posA - posB;
    });
  }, [suites, localOrder]);

  // Drag handlers (pointer-based)
  const handleRowMouseDown = (e: React.MouseEvent, id: number) => {
    e.preventDefault();
    setDraggedId(id);
  };
  // Touch equivalent — mobile fires touchstart/touchmove/touchend, not mousedown/mousemove/mouseup,
  // so the mouse-only handlers above never see a mobile drag at all.
  const handleRowTouchStart = (e: React.TouchEvent, id: number) => {
    setDraggedId(id);
  };

  useEffect(() => {
    if (draggedId === null) return;
    const updateDragOver = (clientY: number) => {
      rowRefs.current.forEach((el, id) => {
        const rect = el.getBoundingClientRect();
        if (clientY >= rect.top && clientY <= rect.bottom && dragOverIdRef.current !== id) {
          dragOverIdRef.current = id;
          setDragOverId(id);
        }
      });
    };
    const commitDrop = () => {
      const targetId = dragOverIdRef.current;
      if (targetId !== null && targetId !== draggedId) {
        // Always derive from orderedSuites, not raw localOrder directly: localOrder can be a stale
        // partial list (e.g. saved back when fewer suites existed), which would make indexOf return -1
        // for any suite added since, silently no-op'ing the drop.
        const base = orderedSuites.map(s => s.id);
        const from = base.indexOf(draggedId);
        const to = base.indexOf(targetId);
        if (from !== -1 && to !== -1) {
          const newOrder = [...base];
          newOrder.splice(from, 1);
          newOrder.splice(to, 0, draggedId);
          setLocalOrder(newOrder);
          localStorage.setItem('hub_suite_order', JSON.stringify(newOrder));
        }
      }
      dragOverIdRef.current = null;
      setDraggedId(null);
      setDragOverId(null);
    };
    const handleMouseMove = (e: MouseEvent) => updateDragOver(e.clientY);
    const handleMouseUp = () => commitDrop();
    const handleTouchMove = (e: TouchEvent) => {
      // Prevent the list from scrolling under the finger while a row is being dragged.
      e.preventDefault();
      updateDragOver(e.touches[0].clientY);
    };
    const handleTouchEnd = () => commitDrop();
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [draggedId, localOrder, orderedSuites]);

  // Form helpers
  const openAdd = () => {
    setEditSuite(null);
    setFormData({ display_name: '', suite_url: '', suite_username: '', suite_password: '' });
    setTestResult(null);
    setFormError('');
    setFormOpen(true);
  };
  const openEdit = (s: Suite) => {
    setEditSuite(s);
    setFormData({ display_name: s.display_name, suite_url: s.suite_url, suite_username: s.suite_username, suite_password: '' });
    setTestResult(null);
    setFormError('');
    setFormOpen(true);
  };

  const handleTestConnection = async () => {
    setFormLoading(true); setTestResult(null);
    try {
      const resp = await axios.post<{ success: boolean; message: string; center_name?: string }>(
        `${HUB_API}/api/v1/suites/test`, formData, { headers }
      );
      setTestResult(resp.data);
      if (resp.data.success && resp.data.center_name && !formData.display_name) {
        setFormData(p => ({ ...p, display_name: resp.data.center_name as string }));
      }
    } catch { setTestResult({ success: false, message: 'Connection failed' }); }
    finally { setFormLoading(false); }
  };

  const handleSaveSuite = async () => {
    setFormLoading(true); setFormError('');
    try {
      if (editSuite) {
        await axios.put(`${HUB_API}/api/v1/suites/${editSuite.id}`, formData, { headers });
      } else {
        await axios.post(`${HUB_API}/api/v1/suites/`, formData, { headers });
      }
      await loadSuites();
      setFormOpen(false); setEditSuite(null);
    } catch (e: any) {
      setFormError(e.response?.data?.detail || 'Failed to save suite');
    } finally { setFormLoading(false); }
  };

  const handleOpenSuite = async (suite: Suite, modality?: string) => {
    try {
      const resp = await axios.post<{ token: string }>(`${HUB_API}/api/v1/suites/${suite.id}/open-token`, {}, { headers });
      setModalToken(resp.data.token || '');
      setModalSuite({ suite, modality });
    } catch { }
  };

  const handleModalClose = () => {
    const closedSuiteId = modalSuite?.suite.id;
    setModalSuite(null); setModalToken('');
    if (closedSuiteId) refreshOneSuite(closedSuiteId); else refreshCounts();
  };

  const getModalRoutes = () => {
    const role = modalSuite?.suite.cached_role || '';
    if (!modalSuite?.modality && ROLE_CARD_ROUTES[role]) return ROLE_CARD_ROUTES[role];
    return ROLE_MODAL_ROUTES[role] || ROLE_MODAL_ROUTES.radiologist;
  };

  const getModalUrl = () => {
    if (!modalSuite || !modalToken) return '';
    const { suite, modality } = modalSuite;
    // Auto: non-touch devices (desktop/laptop) always resolve to Desktop, regardless
    // of window width. Touch devices use the Mobile page — phone-narrow viewports at
    // phone scale, anything wider (13" tablets) at Tablet 13" scale.
    const autoResolvedDisplay: 'desktop' | 'mobile' | 'tablet' = !hasTouchScreen ? 'desktop' : isPhone ? 'mobile' : 'tablet';
    const effectiveDisplay = readingModuleDisplay === 'auto' ? autoResolvedDisplay : readingModuleDisplay;
    // 'tablet' opens the same Mobile page as 'mobile' — the Suite side tells the two
    // apart via the display=tablet query param appended below.
    const useMobile = effectiveDisplay !== 'desktop';
    const routes = getModalRoutes();
    const path = useMobile ? routes.mobile : routes.desktop;
    let url = `${suite.suite_url}${path}?hub_token=${modalToken}`;
    if (modality) url += `&modality=${modality}`;
    if (effectiveDisplay === 'tablet') url += '&display=tablet';
    // CSS env(titlebar-area-*) doesn't carry real values inside Suite's
    // cross-origin iframe below (always serves its fallback there) - this is
    // the one place that DOES have genuine access to the real geometry
    // (Hub's own top-level document, same window the OS buttons are drawn
    // in), so measure it here and pass it through explicitly. Suite reads
    // these via getEmbeddedTitlebarGeometry() (see SuiteHeader.tsx).
    const wco = (navigator as any).windowControlsOverlay;
    if (wco && wco.visible) {
      const rect = wco.getTitlebarAreaRect();
      url += `&wco_h=${Math.round(rect.height)}&wco_x=${Math.round(rect.x)}&wco_w=${Math.round(correctedWcoWidth(rect.width))}`;
    }
    return url;
  };

  // Widget computed values
  const aggregateStats = useMemo(() => {
    const byModality: Record<string, number> = {};
    let total = 0, online = 0, offline = 0, authFailed = 0;
    const byPriority: Record<string, number> = { Routine: 0, Urgent: 0, STAT: 0 };
    orderedSuites.forEach(suite => {
      // Count suite status first
      if (suite.last_status === 'online') online++;
      else if (suite.last_status === 'auth_failed') authFailed++;
      else offline++;

      // Only aggregate counts from online suites — offline/stale data excluded
      if (suite.last_status !== 'online') return;

      const bm = JSON.parse(suite.cached_by_modality || '{}');
      Object.entries(bm).forEach(([mod, cnt]: [string, any]) => {
        byModality[mod] = (byModality[mod] || 0) + (cnt as number);
        total += cnt as number;
      });
      const bp = JSON.parse(suite.cached_by_priority || '{}');
      Object.entries(bp).forEach(([p, cnt]: [string, any]) => {
        if (p in byPriority) byPriority[p] += cnt as number;
        else byPriority.Routine += cnt as number;
      });
    });
    // Aggregate per-modality priority across all online suites
    const byModalityPriority: Record<string, Record<string, number>> = {};
    orderedSuites.forEach(suite => {
      if (suite.last_status !== 'online') return;
      const bmp = JSON.parse(suite.cached_by_modality_priority || '{}');
      Object.entries(bmp).forEach(([mod, priorities]: [string, any]) => {
        if (!byModalityPriority[mod]) byModalityPriority[mod] = { Routine: 0, Urgent: 0, STAT: 0 };
        Object.entries(priorities).forEach(([p, cnt]: [string, any]) => {
          byModalityPriority[mod][p] = (byModalityPriority[mod][p] || 0) + (cnt as number);
        });
      });
    });
    return { byModality, total, online, offline, authFailed, byPriority, byModalityPriority };
  }, [orderedSuites]);

  const clockFormatted = useMemo(() => {
    const match = timezone.match(/UTC([+-]\d+)/);
    const offsetH = match ? parseInt(match[1]) : 0;
    const local = new Date(clockTime.getTime() + offsetH * 3600000);
    const h = local.getUTCHours(), m = local.getUTCMinutes(), s = local.getUTCSeconds();
    const ampm = h >= 12 ? 'PM' : 'AM', h12 = h % 12 || 12;
    const time = `${String(h12).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')} ${ampm}`;
    const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const date = `${DAYS[local.getUTCDay()]}, ${MONTHS[local.getUTCMonth()]} ${local.getUTCDate()}, ${local.getUTCFullYear()}`;
    const greeting = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : h < 21 ? 'Good evening' : 'Good night';
    return { time, date, greeting };
  }, [clockTime, timezone]);

  // Open-Meteo weathercode -> emoji (https://open-meteo.com/en/docs, WMO codes). Native emoji renders
  // with Apple's own colorful glyphs on Mac/iPhone/Safari — no icon assets or extra deps needed.
  const weatherEmoji = useMemo(() => {
    if (!weather) return null;
    const code = weather.code;
    if (code === 0 || code === 1) return '☀️';
    if (code === 2) return '⛅';
    if (code === 3) return '☁️';
    if (code === 45 || code === 48) return '🌫️';
    if ([51, 53, 55, 56, 57].includes(code)) return '🌦️';
    if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return '🌧️';
    if ([71, 73, 75, 77, 85, 86].includes(code)) return '❄️';
    if ([95, 96, 99].includes(code)) return '⛈️';
    return '🌡️';
  }, [weather]);

  const paginatedSuites = orderedSuites.slice((page - 1) * CARDS_PER_PAGE, page * CARDS_PER_PAGE);
  const totalPages = Math.ceil(orderedSuites.length / CARDS_PER_PAGE);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top Bar */}
      <HubHeader
        sx={{
          // Dark mode: background.paper is already a nice on-brand dark tint
          // (same hue as the accent color, just darker) -- leave it alone.
          // Light mode: that same tint formula lands near-white, which reads
          // as a plain, off-brand bar, so use the solid accent color instead,
          // matching Suite Dashboard's own header (its AppBar has no override
          // and defaults to primary.main in both modes).
          bgcolor: (theme) => theme.palette.mode === 'light' ? 'primary.main' : 'background.paper',
          color: (theme) => theme.palette.mode === 'light' ? 'primary.contrastText' : 'text.primary',
        }}
      >
          {/* Draggable region: with Window Controls Overlay active, this row IS
              the title bar - grabbing empty space here needs to move the window
              the way a native title bar would. Buttons below are unaffected
              (drag is not inherited by them; default is no-drag). */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }} style={{ WebkitAppRegion: 'drag' } as unknown as React.CSSProperties}>
            <BrandIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="subtitle2" noWrap sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>{hubName ? `${hubName} - RT Radiology Hub` : 'RT Radiology Hub'}</Typography>
          </Box>
          <Tooltip title="Refresh counts">
            <span>
              <IconButton color="inherit" onClick={refreshCounts} disabled={refreshing} size="small" sx={{ mr: 0.5 }}>
                {refreshing ? <CircularProgress size={16} color="inherit" /> : <Refresh fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}>
            <IconButton color="inherit" onClick={toggleTheme} size="small" sx={{ mr: 0.5 }}>
              {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Settings">
            <IconButton color="inherit" onClick={() => setSettingsOpen(true)} size="small" sx={{ mr: 1.5 }}>
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button color="inherit" startIcon={<ExitToApp fontSize="small" />} onClick={logout} size="small" sx={{ fontSize: '0.75rem' }}>Logout</Button>
      </HubHeader>

      {/* Seamless Widget Bar — single Paper, 4 parts */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Paper elevation={2} sx={{
          p: 2, display: 'flex', gap: isMobile ? 2 : 4, alignItems: 'flex-start',
          flexDirection: isMobile ? 'column' : 'row', flexWrap: isMobile ? 'wrap' : 'nowrap',
        }}>

          {/* Part 1: Clock + Weather + Greeting */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexBasis: isMobile ? '100%' : 'calc(25% - 28px)', flexShrink: 0, width: isMobile ? '100%' : 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ fontFamily: 'monospace', letterSpacing: 2, whiteSpace: 'nowrap' }}>
                  {clockFormatted.time}
                </Typography>
                <Typography variant="caption" color="text.secondary">{clockFormatted.date}</Typography>
              </Box>
              {weather && weatherEmoji && (
                <Tooltip title={`${city}${country ? `, ${country}` : ''}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '3.75rem', lineHeight: 1 }}>{weatherEmoji}</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>{city}</Typography>
                      <Typography variant="body2" color="text.secondary">{weather.temp}°C</Typography>
                    </Box>
                  </Box>
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" sx={{ mt: 0.5, color: 'primary.main' }}>
              {clockFormatted.greeting}{hubName ? `, ${hubName}` : ''}
            </Typography>
          </Box>

          {/* Part 2: Total Pending */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexShrink: 0, flexBasis: isMobile ? '100%' : 'calc(25% - 24px)', width: isMobile ? '100%' : 'auto' }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 'bold', letterSpacing: 1, fontSize: '0.65rem', mb: 0.5 }}>
              PENDING
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 0.5, mb: 0.5 }}>
              {Object.entries(aggregateStats.byModality).sort(([, a], [, b]) => (b as number) - (a as number)).map(([mod, cnt]) => {
                const mp = aggregateStats.byModalityPriority?.[mod] || {};
                const chipColor = (mp.STAT || 0) > 0 ? '#F44336' : (mp.Urgent || 0) > 0 ? '#FF9800' : '#4CAF50';
                return (
                  <Chip key={mod} label={`${mod} ${cnt}`} size="small"
                    sx={{ bgcolor: chipColor, color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }} />
                );
              })}
              {Object.keys(aggregateStats.byModality).length === 0 && (
                <Typography variant="caption" color="success.main" fontWeight="bold">✓ All clear</Typography>
              )}
            </Box>
            <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1 }}>
              {aggregateStats.total} <Typography component="span" variant="caption" color="text.secondary">studies</Typography>
            </Typography>
          </Box>

          {/* Part 3: Priority Breakdown */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flexShrink: 0, flexBasis: isMobile ? '100%' : 'calc(25% - 24px)', width: isMobile ? '100%' : 'auto' }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 'bold', letterSpacing: 1, fontSize: '0.65rem', mb: 0.5 }}>
              PRIORITY
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 0.5, mb: 0.5 }}>
              <Chip label={`STAT ${aggregateStats.byPriority?.STAT ?? 0}`} size="small"
                sx={{ bgcolor: '#F44336', color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }} />
              <Chip label={`URGENT ${aggregateStats.byPriority?.Urgent ?? 0}`} size="small"
                sx={{ bgcolor: '#FF9800', color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }} />
              <Chip label={`ROUTINE ${aggregateStats.byPriority?.Routine ?? 0}`} size="small"
                sx={{ bgcolor: '#4CAF50', color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }} />
            </Box>
          </Box>

          {/* Part 4: Suites Status — compact */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexShrink: 0, flexBasis: isMobile ? '100%' : 'auto', width: isMobile ? '100%' : 'auto' }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 'bold', letterSpacing: 1, fontSize: '0.65rem', mb: 0.5 }}>
              SUITES
            </Typography>
            {aggregateStats.online > 0 && <Typography variant="caption" color="success.main">✅ {aggregateStats.online} online</Typography>}
            {aggregateStats.authFailed > 0 && <Typography variant="caption" color="warning.main">⚠️ {aggregateStats.authFailed} check creds</Typography>}
            {aggregateStats.offline > 0 && <Typography variant="caption" color="error.main">🔴 {aggregateStats.offline} offline</Typography>}
            {orderedSuites.length === 0 && <Typography variant="caption" color="text.secondary">No suites</Typography>}
          </Box>

          {/* Part 5: Quote — takes remaining space */}
          <Box sx={{
            flex: isMobile ? '0 0 auto' : 1, display: 'flex', alignItems: 'center', minWidth: 0,
            position: 'relative', alignSelf: 'stretch', width: isMobile ? '100%' : 'auto',
            minHeight: isMobile ? 40 : 'auto',
          }}>
            {/* Sticky control stack — fixed top-right, doesn't move when quote/hidden state changes */}
            <Box sx={{ position: 'absolute', top: 0, right: 0, display: 'flex', flexDirection: 'column', zIndex: 1 }}>
              <Tooltip title={quoteHidden ? 'Show quote' : 'Hide quote'}>
                <IconButton size="small" onClick={() => {
                  const next = !quoteHidden;
                  setQuoteHidden(next);
                  localStorage.setItem('hub_quote_hidden', String(next));
                }} sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}>
                  {quoteHidden ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                </IconButton>
              </Tooltip>
              {!quoteHidden && (
                <Tooltip title="Random quote">
                  <IconButton size="small" onClick={() => setQuoteIndex(i => pickRandomQuoteIndex(i))} sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}>
                    <Shuffle fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {!quoteHidden && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, pr: 5 }}>
                <Typography variant="body2" fontStyle="italic" color="text.secondary" sx={{ lineHeight: 1.5, mb: 0.25 }} noWrap={false}>
                  "{QUOTES[quoteIndex].text}"
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  — {QUOTES[quoteIndex].author}
                </Typography>
              </Box>
            )}
          </Box>

        </Paper>
      </Box>

      {/* Card Grid */}
      <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
        ) : orderedSuites.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, gap: 2 }}>
            <Typography variant="h6" color="text.secondary">No suites added yet</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => { setSettingsOpen(true); }}>
              Open Settings to Add a Suite
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ minHeight: 408 }}>
              <Grid container spacing={2}>
                {paginatedSuites.map(suite => (
                  <Grid item xs={12} sm={6} md={3} key={suite.id}>
                    <SuiteCard suite={suite} onOpen={handleOpenSuite} />
                  </Grid>
                ))}
              </Grid>
            </Box>
            {/* Bottom Widgets — MUI Grid matching card grid spacing */}
            <Grid container spacing={2} sx={{ mt: 0 }}>
              {/* Notes — aligned with card cols 1+2 */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth multiline minRows={isPhone ? 6 : 13} maxRows={isPhone ? 6 : 13}
                  size="small"
                  label="Notes"
                  InputLabelProps={{ shrink: true }}
                  placeholder="Daily reminders, STAT alerts, follow-ups..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  onBlur={async () => {
                    setNotesSaving(true);
                    try {
                      await axios.put(`${HUB_API}/api/v1/settings/`, { notes }, { headers });
                    } catch {}
                    finally { setNotesSaving(false); }
                  }}
                  sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' }, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                />
              </Grid>
              {/* Reminders (backed by the notes2 field) — aligned with card col 3 */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth multiline minRows={isPhone ? 6 : 13} maxRows={isPhone ? 6 : 13}
                  size="small"
                  label="Reminders"
                  InputLabelProps={{ shrink: true }}
                  placeholder="Follow-ups, callbacks, to-dos..."
                  value={notes2}
                  onChange={e => setNotes2(e.target.value)}
                  onBlur={async () => {
                    setNotes2Saving(true);
                    try {
                      await axios.put(`${HUB_API}/api/v1/settings/`, { notes2 }, { headers });
                    } catch {}
                    finally { setNotes2Saving(false); }
                  }}
                  sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' }, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                />
              </Grid>
              {/* Calendar — visual only, fixed height matching the Notes/Reminders 13-row box, never autoexpands */}
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ height: isMobile ? 340 : '100%', display: 'flex' }}>
                  {/* Prev-month arrow — spans the full widget height */}
                  <Box
                    onClick={() => setCalMonth(m => { if (m === 0) { setCalYear(y => y - 1); return 11; } return m - 1; })}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5,
                      cursor: 'pointer', color: 'text.disabled', '&:hover': { color: 'text.secondary' },
                    }}
                  >
                    <KeyboardArrowLeft fontSize="small" />
                  </Box>

                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', px: 'calc(100%/14 - 9px)' }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'text.primary' }}>
                        {MONTH_NAMES[calMonth]} {calYear}
                      </Typography>
                      <Typography
                        onClick={() => { const now = new Date(); setCalMonth(now.getMonth()); setCalYear(now.getFullYear()); }}
                        sx={{ fontSize: '0.75rem', color: 'text.disabled', cursor: 'pointer', '&:hover': { color: 'text.secondary' } }}
                      >
                        Today
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mt: 2, flexShrink: 0 }}>
                      {WEEKDAY_LABELS.map((d, i) => (
                        <Typography key={i} sx={{ textAlign: 'center', fontSize: '0.65rem', color: 'text.disabled' }}>
                          {d}
                        </Typography>
                      ))}
                    </Box>
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'repeat(6, 1fr)' }}>
                      {getCalendarCells(calYear, calMonth).map((day, i) => {
                        const now = new Date();
                        const isToday = day !== null && calYear === now.getFullYear() && calMonth === now.getMonth() && day === now.getDate();
                        return (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {day !== null && (
                              <Box sx={{
                                width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                borderRadius: 1,
                                bgcolor: isToday ? 'primary.main' : 'transparent',
                              }}>
                                <Typography sx={{ fontSize: '0.8rem', lineHeight: 1, color: isToday ? 'primary.contrastText' : 'text.secondary', fontWeight: isToday ? 'bold' : 400 }}>
                                  {day}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Next-month arrow — spans the full widget height */}
                  <Box
                    onClick={() => setCalMonth(m => { if (m === 11) { setCalYear(y => y + 1); return 0; } return m + 1; })}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5,
                      cursor: 'pointer', color: 'text.disabled', '&:hover': { color: 'text.secondary' },
                    }}
                  >
                    <KeyboardArrowRight fontSize="small" />
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ flex: 1 }} />
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination count={totalPages} page={page} onChange={(_: any, v: number) => setPage(v)} />
              </Box>
            )}
          </>
        )}
      </Box>


      {/* Statistics Modal */}
      <Dialog open={statsOpen} onClose={() => setStatsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight="bold">Statistics</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={fetchCensus} disabled={statsLoading} sx={{ mr: 0.5 }}>
                  {statsLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={() => setStatsOpen(false)}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Period selector */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {(['today','week','month','year'] as const).map(p => (
              <Button key={p} size="small" variant={statsPeriod === p ? 'contained' : 'outlined'}
                onClick={() => setStatsPeriod(p)} sx={{ textTransform: 'capitalize' }}>
                {p === 'today' ? 'Today' : p === 'week' ? 'This Week' : p === 'month' ? 'Month' : 'Year'}
              </Button>
            ))}
            {(statsPeriod === 'month' || statsPeriod === 'year') && (
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                {Array.from({length: 5}, (_, i) => new Date().getFullYear() - 1 + i).map(y => (
                  <Button key={y} size="small"
                    variant={statsYear === y ? 'contained' : 'outlined'}
                    onClick={() => setStatsYear(y)}
                    sx={{ minWidth: 52, px: 1 }}>
                    {y}
                  </Button>
                ))}
              </Box>
            )}
            {statsPeriod === 'month' && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                  <Button key={i+1} size="small"
                    variant={statsMonth === i+1 ? 'contained' : 'outlined'}
                    onClick={() => setStatsMonth(i+1)}
                    sx={{ minWidth: 40, px: 0.5, fontSize: '0.75rem' }}>
                    {m}
                  </Button>
                ))}
              </Box>
            )}
          </Box>

          {/* Statistics Table */}
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            (() => {
              // Sort by localOrder (settings drag order)
              const sortedData = localOrder.length > 0
                ? [...statsData].sort((a, b) => {
                    const ia = localOrder.indexOf(a.suite_id);
                    const ib = localOrder.indexOf(b.suite_id);
                    if (ia === -1 && ib === -1) return 0;
                    if (ia === -1) return 1;
                    if (ib === -1) return -1;
                    return ia - ib;
                  })
                : statsData;

              // Collect all modalities across all suites
              const allMods = Array.from(new Set(
                sortedData.flatMap(s => Object.keys(s.by_modality || {}))
              )).sort();
              
              // Totals
              const totals = { total: 0, seriesViews: 0, completed: 0, pending: 0, by_modality: {} as Record<string, number> };
              sortedData.forEach(s => {
                totals.total += s.total || 0;
                totals.seriesViews += s.series_views || 0;
                totals.completed += s.completed || 0;
                totals.pending += s.pending || 0;
                allMods.forEach(m => { totals.by_modality[m] = (totals.by_modality[m] || 0) + (s.by_modality?.[m] || 0); });
              });

              return (
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.1)' }}>
                        <th style={{ textAlign: 'left', padding: '8px', fontWeight: 'bold' }}>Institution</th>
                        {allMods.map(m => <th key={m} style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold' }}>{m}</th>)}
                        <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold' }}>Series/Views</th>
                        <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', color: '#FF9800' }}>Pending</th>
                        <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', color: '#4CAF50' }}>Completed</th>
                        <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold' }}>Total Studies</th>
                        <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold' }}>Total Series/Views</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedData.map(suite => (
                        <tr key={suite.suite_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', backgroundColor: suite.error ? 'rgba(255,152,0,0.08)' : undefined }}>
                          <td style={{ padding: '6px 8px' }}>
                            {suite.display_name}
                            {suite.error && (
                              <Tooltip title={`Failed to load this period's data: ${suite.error}`}>
                                <Warning fontSize="inherit" sx={{ color: '#FF9800', ml: 0.5, verticalAlign: 'middle' }} />
                              </Tooltip>
                            )}
                          </td>
                          {allMods.map(m => <td key={m} style={{ textAlign: 'center', padding: '6px 8px' }}>{suite.by_modality?.[m] || '—'}</td>)}
                          <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                            {suite.series_views || 0}
                            {suite.series_views_partial && (
                              <Tooltip title={`Still computing ${suite.series_views_pending} more studies' views for this period - refresh again shortly for the final number`}>
                                <HourglassEmpty fontSize="inherit" sx={{ color: '#2196F3', ml: 0.5, verticalAlign: 'middle' }} />
                              </Tooltip>
                            )}
                          </td>
                          <td style={{ textAlign: 'center', padding: '6px 8px', color: '#FF9800', fontWeight: 'bold' }}>{suite.pending || 0}</td>
                          <td style={{ textAlign: 'center', padding: '6px 8px', color: '#4CAF50', fontWeight: 'bold' }}>{suite.completed || 0}</td>
                          <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 'bold' }}>{suite.total || 0}</td>
                          <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 'bold' }}>{suite.series_views || 0}</td>
                        </tr>
                      ))}
                      {/* Totals row */}
                      <tr style={{ borderTop: '2px solid rgba(255,255,255,0.2)', fontWeight: 'bold' }}>
                        <td style={{ padding: '8px' }}>TOTAL</td>
                        {allMods.map(m => <td key={m} style={{ textAlign: 'center', padding: '8px' }}>{totals.by_modality[m] || '—'}</td>)}
                        <td style={{ textAlign: 'center', padding: '8px' }}>{totals.seriesViews}</td>
                        <td style={{ textAlign: 'center', padding: '8px', color: '#FF9800' }}>{totals.pending}</td>
                        <td style={{ textAlign: 'center', padding: '8px', color: '#4CAF50' }}>{totals.completed}</td>
                        <td style={{ textAlign: 'center', padding: '8px' }}>{totals.total}</td>
                        <td style={{ textAlign: 'center', padding: '8px' }}>{totals.seriesViews}</td>
                      </tr>
                    </tbody>
                  </table>
                  {sortedData.length === 0 && !statsLoading && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No data available for this period
                    </Typography>
                  )}
                </Box>
              );
            })()
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Drawer */}
      <Drawer anchor="right" open={settingsOpen} onClose={() => setSettingsOpen(false)}
        PaperProps={{ sx: { width: 380 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, height: 48, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Settings</Typography>
          <IconButton size="small" onClick={() => setSettingsOpen(false)}><Close /></IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', mb: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            onClick={() => setSuitesExpanded(p => !p)}
          >
            {suitesExpanded ? <KeyboardArrowDown sx={{ mr: 0.5 }} /> : <KeyboardArrowRight sx={{ mr: 0.5 }} />}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
              Suites {orderedSuites.length > 0 ? `(${orderedSuites.length})` : ''}
            </Typography>
            <Button size="small" variant="outlined" startIcon={<Add />}
              onClick={e => { e.stopPropagation(); openAdd(); }} sx={{ mr: 0.5 }}>
              Add Suite
            </Button>
          </Box>

          <Collapse in={suitesExpanded}>
          {orderedSuites.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              No suites yet. Add your first suite above.
            </Typography>
          ) : (
            <List disablePadding>
              {orderedSuites.map((suite, idx) => (
                <React.Fragment key={suite.id}>
                  {idx > 0 && <Divider />}
                  <ListItem
                    disablePadding
                    ref={(el: HTMLElement | null) => {
                      if (el) rowRefs.current.set(suite.id, el);
                      else rowRefs.current.delete(suite.id);
                    }}
                    onMouseDown={e => handleRowMouseDown(e, suite.id)}
                    onTouchStart={e => handleRowTouchStart(e, suite.id)}
                    sx={{
                      py: 1, px: 0.5, cursor: 'grab', userSelect: 'none', touchAction: 'none',
                      bgcolor: dragOverId === suite.id ? 'action.hover' : 'transparent',
                      opacity: draggedId === suite.id ? 0.4 : 1,
                      transition: 'background-color 0.15s',
                      '&:active': { cursor: 'grabbing' }
                    }}
                  >
                    <DragIndicator sx={{ color: 'text.disabled', mr: 1, fontSize: 20, flexShrink: 0 }} />
                    <ListItemText
                      primary={suite.display_name || suite.cached_center_name}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium', noWrap: true }}
                      sx={{ flex: 1, minWidth: 0, mr: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }} onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => openEdit(suite)} title="Edit">
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteSuite(suite)} title="Remove">
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}

          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            Drag rows to reorder cards on the dashboard
          </Typography>
          </Collapse>

          {/* Appearance section */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            onClick={() => setAppearanceExpanded(p => !p)}
          >
            {appearanceExpanded ? <KeyboardArrowDown sx={{ mr: 0.5 }} /> : <KeyboardArrowRight sx={{ mr: 0.5 }} />}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
              Appearance
            </Typography>
          </Box>

          <Collapse in={appearanceExpanded}>
            <Box sx={{ mt: 1.5, mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Accent color</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {(Object.keys(THEME_COLORS) as ThemeColorName[]).map((key) => {
                  const selected = themeColor === key;
                  return (
                    <Tooltip title={THEME_COLORS[key].label} key={key}>
                      <Box
                        onClick={async () => {
                          setThemeColor(key);
                          setAppearanceSaving(true);
                          try {
                            await axios.put(`${HUB_API}/api/v1/settings/`, { theme_color: key }, { headers });
                          } catch {}
                          finally { setAppearanceSaving(false); }
                        }}
                        sx={{
                          width: 32, height: 32, borderRadius: '50%',
                          bgcolor: THEME_COLORS[key][mode], cursor: 'pointer',
                          border: selected ? '3px solid' : '2px solid',
                          borderColor: selected ? 'text.primary' : 'divider',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'transform 0.1s',
                          '&:hover': { transform: 'scale(1.1)' },
                        }}
                      >
                        {selected && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'common.white', boxShadow: '0 0 0 1px rgba(0,0,0,0.3)' }} />
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
              {appearanceSaving && <Typography variant="caption" color="text.secondary">Saving…</Typography>}
            </Box>
          </Collapse>

          {/* Display section */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            onClick={() => setDisplayExpanded(p => !p)}
          >
            {displayExpanded ? <KeyboardArrowDown sx={{ mr: 0.5 }} /> : <KeyboardArrowRight sx={{ mr: 0.5 }} />}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
              Display
            </Typography>
          </Box>

          <Collapse in={displayExpanded}>
            <Box sx={{ mt: 1.5, mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Suite Page Display</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={readingModuleDisplay}
                onChange={(_e, value) => {
                  if (value === 'auto' || value === 'desktop' || value === 'mobile' || value === 'tablet') {
                    setReadingModuleDisplay(value);
                    localStorage.setItem('hub_reading_module_display', value);
                  }
                }}
              >
                <ToggleButton value="auto">Auto</ToggleButton>
                <ToggleButton value="desktop">Desktop</ToggleButton>
                <ToggleButton value="mobile">Mobile</ToggleButton>
                <ToggleButton value="tablet">Tablet 13"</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.disabled">
                {readingModuleDisplay === 'auto'
                  ? "Auto matches this device by touch capability, not window size — opening a suite uses its Mobile page on phones, its Tablet 13\" page on other touch devices, and its Desktop page on anything without touch."
                  : readingModuleDisplay === 'mobile'
                  ? 'Opening a suite always uses its Mobile page, on any device.'
                  : readingModuleDisplay === 'tablet'
                  ? 'Opening a suite always uses its Mobile page, sized and laid out for a 13" tablet, on any device.'
                  : 'Opening a suite always uses its Desktop page, on any device.'}
              </Typography>
            </Box>
          </Collapse>

          {/* Hub Settings section */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            onClick={() => setHubSettingsExpanded(p => !p)}
          >
            {hubSettingsExpanded ? <KeyboardArrowDown sx={{ mr: 0.5 }} /> : <KeyboardArrowRight sx={{ mr: 0.5 }} />}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>Hub Name</Typography>
          </Box>

          <Collapse in={hubSettingsExpanded}>
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                  label="Hub Name"
                  value={hubNameInput}
                  onChange={e => setHubNameInput(e.target.value)}
                  size="small"
                  fullWidth
                  
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={hubNameSaving || !hubNameInput.trim()}
                  onClick={async () => {
                    setHubNameSaving(true);
                    try {
                      await axios.put(`${HUB_API}/api/v1/settings/`, { hub_name: hubNameInput }, { headers });
                      setHubName(hubNameInput);
                    } catch {}
                    finally { setHubNameSaving(false); }
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Save
                </Button>
              </Box>
            </Collapse>

          {/* Timezone section */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            onClick={() => setTimezoneExpanded((p: boolean) => !p)}
          >
            {timezoneExpanded ? <KeyboardArrowDown sx={{ mr: 0.5 }} /> : <KeyboardArrowRight sx={{ mr: 0.5 }} />}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>Timezone</Typography>
          </Box>
          <Collapse in={timezoneExpanded}>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>UTC Offset</InputLabel>
                <Select
                  label="UTC Offset"
                  value={hubTimezoneInput}
                  onChange={(e: any) => setHubTimezoneInput(e.target.value)}
                >
                  {Array.from({length: 25}, (_, i) => i - 12).map(offset => {
                    const label = offset === 0 ? 'UTC' : offset > 0 ? `UTC+${offset}` : `UTC${offset}`;
                    return <MenuItem key={label} value={label}>{label}</MenuItem>;
                  })}
                </Select>
              </FormControl>
              <Button variant="contained" size="small" sx={{ alignSelf: 'flex-start' }}
                onClick={async () => {
                  try {
                    await axios.put(`${HUB_API}/api/v1/settings/`, { timezone: hubTimezoneInput }, { headers });
                    setTimezone(hubTimezoneInput);
                  } catch {}
                }}>Save</Button>
            </Box>
          </Collapse>

          {/* Weather section — independent of Timezone; drives the icon+city+temp beside the clock */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            onClick={() => setWeatherExpanded((p: boolean) => !p)}
          >
            {weatherExpanded ? <KeyboardArrowDown sx={{ mr: 0.5 }} /> : <KeyboardArrowRight sx={{ mr: 0.5 }} />}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>Weather</Typography>
          </Box>
          <Collapse in={weatherExpanded}>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={locationMode}
                onChange={async (_e, value) => {
                  if (value !== 'auto' && value !== 'manual') return;
                  setLocationMode(value);
                  if (value === 'manual' && citySelected) {
                    setCity(citySelected.name);
                    setCountry(countryInput);
                    setLatitude(citySelected.latitude);
                    setLongitude(citySelected.longitude);
                  }
                  try {
                    await axios.put(`${HUB_API}/api/v1/settings/`, { location_mode: value }, { headers });
                  } catch {}
                }}
              >
                <ToggleButton value="auto">Auto</ToggleButton>
                <ToggleButton value="manual">Manual</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.disabled">
                {locationMode === 'auto'
                  ? "Auto detects each viewer's own location from their IP address."
                  : 'Manual shows the same fixed city to everyone, chosen below.'}
              </Typography>
              {locationMode === 'manual' && (
                <>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Country</InputLabel>
                    <Select
                      label="Country"
                      value={countryInput}
                      onChange={(e: any) => {
                        setCountryInput(e.target.value);
                        setCitySelected(null);
                        setCityQuery('');
                        setCityOptions([]);
                      }}
                    >
                      {COUNTRIES.map(c => <MenuItem key={c.code} value={c.name}>{c.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Autocomplete
                    size="small"
                    options={cityOptions}
                    loading={cityOptionsLoading}
                    value={citySelected}
                    inputValue={cityQuery}
                    onInputChange={(_e, value) => setCityQuery(value)}
                    onChange={(_e, value) => setCitySelected(value)}
                    getOptionLabel={(option) => option.admin1 ? `${option.name}, ${option.admin1}` : option.name}
                    isOptionEqualToValue={(a, b) => a.name === b.name && a.latitude === b.latitude && a.longitude === b.longitude}
                    noOptionsText={countryInput ? 'Type to search cities…' : 'Pick a Country first'}
                    disabled={!countryInput}
                    renderInput={(params) => <TextField {...params} label="City" placeholder="Start typing…" />}
                  />
                  <Button variant="contained" size="small" sx={{ alignSelf: 'flex-start' }}
                    disabled={!citySelected}
                    onClick={async () => {
                      if (!citySelected) return;
                      try {
                        await axios.put(`${HUB_API}/api/v1/settings/`, {
                          city: citySelected.name,
                          country: countryInput,
                          latitude: citySelected.latitude,
                          longitude: citySelected.longitude,
                        }, { headers });
                        setCity(citySelected.name);
                        setCountry(countryInput);
                        setLatitude(citySelected.latitude);
                        setLongitude(citySelected.longitude);
                      } catch {}
                    }}>Save</Button>
                </>
              )}
            </Box>
          </Collapse>

          {/* Statistics — opens the Statistics modal directly, doesn't expand inline */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            onClick={() => setStatsOpen(true)}
          >
            <KeyboardArrowRight sx={{ mr: 0.5 }} />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
              Statistics
            </Typography>
          </Box>

          {/* Account section */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            onClick={() => setAccountExpanded(p => !p)}
          >
            {accountExpanded ? <KeyboardArrowDown sx={{ mr: 0.5 }} /> : <KeyboardArrowRight sx={{ mr: 0.5 }} />}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>Account</Typography>
          </Box>
          <Collapse in={accountExpanded}>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {accountError && <Alert severity="error" onClose={() => setAccountError('')}>{accountError}</Alert>}
              {accountSuccess && <Alert severity="success" onClose={() => setAccountSuccess('')}>{accountSuccess}</Alert>}
              <TextField label="Current Password" type="password" size="small" fullWidth
                value={accountCurrentPassword} onChange={e => setAccountCurrentPassword(e.target.value)} />
              <TextField label="New Username" type="text" size="small" fullWidth
                helperText="Optional - leave blank to keep current username"
                value={accountNewUsername} onChange={e => setAccountNewUsername(e.target.value)} />
              <TextField label="New Password" type="password" size="small" fullWidth
                helperText="Optional - minimum 4 characters"
                value={accountNewPassword} onChange={e => setAccountNewPassword(e.target.value)} />
              <TextField label="Confirm New Password" type="password" size="small" fullWidth
                value={accountConfirmPassword} onChange={e => setAccountConfirmPassword(e.target.value)} />
              <Button
                variant="contained"
                size="small"
                disabled={accountSaving || !accountCurrentPassword || (!accountNewUsername && !accountNewPassword)}
                sx={{ alignSelf: 'flex-start' }}
                onClick={async () => {
                  setAccountError(''); setAccountSuccess('');
                  if (accountNewUsername && accountNewUsername.length < 4) {
                    setAccountError('Username must be at least 4 characters'); return;
                  }
                  if (accountNewPassword && accountNewPassword.length < 4) {
                    setAccountError('Password must be at least 4 characters'); return;
                  }
                  if (accountNewPassword && accountNewPassword !== accountConfirmPassword) {
                    setAccountError('New password and confirmation do not match'); return;
                  }
                  setAccountSaving(true);
                  try {
                    await axios.put(`${HUB_API}/api/v1/auth/me`, {
                      current_password: accountCurrentPassword,
                      new_username: accountNewUsername || undefined,
                      new_password: accountNewPassword || undefined,
                    }, { headers });
                    setAccountSuccess('Account updated successfully');
                    setAccountCurrentPassword(''); setAccountNewUsername('');
                    setAccountNewPassword(''); setAccountConfirmPassword('');
                  } catch (e: any) {
                    setAccountError(e.response?.data?.detail || 'Failed to update account');
                  } finally {
                    setAccountSaving(false);
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </Collapse>

          {hubVersion && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
              Hub v{hubVersion}
            </Typography>
          )}
        </Box>
      </Drawer>

      {/* Add / Edit Dialog */}
      <Dialog open={formOpen} onClose={() => { setFormOpen(false); setEditSuite(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editSuite ? 'Edit Suite' : 'Add Suite'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          {testResult && <Alert severity={testResult.success ? 'success' : 'error'}>{testResult.message}</Alert>}
          <TextField label="Display Name" value={formData.display_name}
            onChange={e => setFormData(p => ({ ...p, display_name: e.target.value }))}
            size="small" helperText="Optional - auto-filled from suite" sx={{ mt: 1.5 }} />
          <TextField label="Suite URL" value={formData.suite_url}
            onChange={e => setFormData(p => ({ ...p, suite_url: e.target.value }))}
            size="small" placeholder="https://your-clinic.example.com" required />
          <TextField label="Username" value={formData.suite_username}
            onChange={e => setFormData(p => ({ ...p, suite_username: e.target.value }))}
            size="small" required />
          <TextField label="Password" value={formData.suite_password}
            onChange={e => setFormData(p => ({ ...p, suite_password: e.target.value }))}
            size="small" type="password" required={!editSuite}
            helperText={editSuite ? 'Leave blank to keep current password' : ''} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTestConnection} disabled={formLoading || !formData.suite_url || !formData.suite_username}>
            Test Connection
          </Button>
          <Button onClick={() => { setFormOpen(false); setEditSuite(null); }}>Cancel</Button>
          <Button onClick={handleSaveSuite} variant="contained" disabled={formLoading}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteSuite} onClose={() => setDeleteSuite(null)}>
        <DialogTitle>Remove Suite</DialogTitle>
        <DialogContent>
          <Typography>Remove "{deleteSuite?.display_name || deleteSuite?.cached_center_name}" from your hub?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSuite(null)}>Cancel</Button>
          <Button color="error" onClick={async () => {
            if (deleteSuite) {
              await axios.delete(`${HUB_API}/api/v1/suites/${deleteSuite.id}`, { headers });
              setDeleteSuite(null);
              loadSuites();
            }
          }}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Reading Modal */}
      <Dialog open={!!modalSuite} onClose={handleModalClose} fullScreen>
        {/* Chrome (iOS and Android) ignores the viewport meta zoom-lock above by design
            (Safari/WebKit still honors it) - touch-action is the actual cross-browser way
            to stop a native pinch-zoom gesture starting here. The nested Suite iframe (and
            its own further-nested OHIF iframe) are separate documents, unaffected. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', touchAction: 'pan-x pan-y', position: 'relative' }}>
          {isMobile ? (
            <HubHeader
              sx={{
                bgcolor: (theme) => theme.palette.mode === 'light' ? 'primary.main' : 'background.paper',
                color: (theme) => theme.palette.mode === 'light' ? 'primary.contrastText' : 'text.primary',
              }}
            >
              <Typography
                variant="subtitle2"
                noWrap
                sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}
              >
                {modalSuite?.suite.display_name || modalSuite?.suite.cached_center_name}
                {modalSuite?.modality ? ` - ${modalSuite.modality}` : ''}
              </Typography>
              <IconButton color="inherit" size="small" onClick={handleModalClose}><Close fontSize="small" /></IconButton>
            </HubHeader>
          ) : (
            // This pill is a zero-layout-cost overlay (position: absolute) by
            // design - it must NOT reserve real flow space, otherwise the
            // Suite iframe below gets pushed down and loses OHIF viewer
            // height for nothing. It floats as a layer ABOVE Suite's own
            // header row (which is itself WCO-aware now, see SuiteHeader.tsx
            // + ReadingModalViewer.tsx) - sizing/position intentionally
            // unchanged from the original design (autoshrink to content,
            // capped at 20% width, fixed 30px tall, true center).
            <Tooltip title={`${modalSuite?.suite.display_name || modalSuite?.suite.cached_center_name || ''}${modalSuite?.modality ? ` - ${modalSuite.modality}` : ''}`}>
              <Box
                onClick={handleModalClose}
                // Plain divs sitting inside the OS-reported title-bar area
                // default to being part of the window's drag region (unlike
                // real <button> elements, which Chromium exempts automatically)
                // - without this, only a sliver of this box's edges actually
                // received clicks, the rest silently started a window drag.
                style={{ WebkitAppRegion: 'no-drag' } as unknown as React.CSSProperties}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 'fit-content',
                  maxWidth: '20%',
                  // Hub is the top-level document, so unlike Suite's iframe
                  // this env() genuinely resolves to the real native row
                  // height when WCO is active (safe now that the spacer is
                  // gone and no-drag is set - those were the earlier problems,
                  // not this). Falls back to the original fixed 30px otherwise.
                  height: 'env(titlebar-area-height, 30px)',
                  zIndex: 1300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  px: 1,
                  cursor: 'pointer',
                  borderRadius: '0 0 4px 4px',
                  bgcolor: (theme) => theme.palette.mode === 'light' ? 'primary.main' : 'background.paper',
                  color: (theme) => theme.palette.mode === 'light' ? 'primary.contrastText' : 'text.primary',
                  boxShadow: 2,
                  '&:hover': { opacity: 0.9 },
                }}
              >
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ flex: 1, minWidth: 0, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}
                >
                  {modalSuite?.suite.display_name || modalSuite?.suite.cached_center_name}
                  {modalSuite?.modality ? ` - ${modalSuite.modality}` : ''}
                </Typography>
                <Close fontSize="small" sx={{ flexShrink: 0 }} />
              </Box>
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }}>
            {modalSuite && (
              <iframe
                ref={modalIframeRef}
                src={getModalUrl()}
                // Drag-region (-webkit-app-region) is a top-level-document-only
                // concept - Suite's own no-drag marks on its internal buttons
                // (ArrowBack etc.) have zero effect on the real OS drag
                // computation, since it never looks inside iframes. The only
                // place this can actually be fixed is here: exempt the whole
                // iframe element itself, in Hub's own top-level document.
                style={{ width: '100%', height: '100%', border: 'none', touchAction: 'pan-x pan-y', WebkitAppRegion: 'no-drag' } as unknown as React.CSSProperties}
                title={`${modalSuite.suite.display_name} ${getModalRoutes().title}`}
                allow="clipboard-write"
              />
            )}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default HubDashboard;
